<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { useBillingMe } from "../api/use-billing";
import { aiMessages } from "../i18n/ai";
import { trialDaysLeft } from "../offer";

/**
 * A FAIXA DE TRIAL — "faltam X dias", no topo (plano §10.2).
 *
 * Desenha-se apenas quando o servidor manda `trial`: quem não está em
 * trial nunca vê um pixel disto, e um servidor de antes do trial simplesmente
 * não manda o campo. Expirado, a faixa vira a frase de fim com a porta para
 * `/subscribe` — a parede é a do `no_plan`, e nada é apagado.
 *
 * Dias de CALENDÁRIO: termina amanhã é "falta 1 dia", termina hoje é "último dia".
 */
const props = defineProps<{ enabled?: boolean }>();

const { data: me } = useBillingMe(() => props.enabled !== false);

const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: aiMessages });

const endsAt = computed(() => {
	const raw = me.value?.trial?.endsAt;
	return raw ? new Date(raw) : null;
});

/**
 * O FIM é do servidor (o relógio que suspende); a CONTAGEM é em dias de calendário
 * no fuso do workspace, o mesmo dia que as outras telas escrevem (QA 16).
 */
const daysLeft = computed(() => (me.value?.trial ? trialDaysLeft(me.value.trial) : undefined));

const phrase = computed(() => {
	if (daysLeft.value === undefined) return null;
	if (daysLeft.value === null) return t("trial.ended");
	if (daysLeft.value === 0) return t("trial.lastDay");
	return t("trial.daysLeft", { n: daysLeft.value }, daysLeft.value);
});
</script>

<template>
	<div
		v-if="phrase"
		class="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-border bg-surface-raised px-4 py-1.5 text-center text-xs"
		data-testid="trial-banner">
		<span class="font-medium text-fg">{{ phrase }}</span>
		<span v-if="daysLeft !== undefined && (daysLeft ?? 0) <= 1" class="text-fg-muted">{{ t("trial.hint") }}</span>
		<RouterLink to="/subscribe" class="text-accent hover:underline">{{ t("trial.cta") }}</RouterLink>
	</div>
</template>
